import CallRoundedIcon from '@mui/icons-material/CallRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getSupportContactHistory } from 'src/supportPortal/services/supportCommunicationLogService'

dayjs.extend(utc)
dayjs.extend(relativeTime)

const PAGE_SIZE = 50

function getChannelMeta(channel) {
  const normalized = `${channel || ''}`.toLowerCase()
  if (normalized === 'whatsapp') {
    return { label: 'WhatsApp', color: '#128c7e', icon: <WhatsAppIcon sx={{ fontSize: 16 }} /> }
  }
  if (normalized === 'email') {
    return { label: 'Email', color: '#2563eb', icon: <EmailRoundedIcon sx={{ fontSize: 16 }} /> }
  }
  if (normalized === 'voice' || normalized === 'call' || normalized === 'phone') {
    return { label: 'Call', color: '#7c3aed', icon: <CallRoundedIcon sx={{ fontSize: 16 }} /> }
  }
  return { label: channel || 'Activity', color: '#64748b', icon: <ForumRoundedIcon sx={{ fontSize: 16 }} /> }
}

function getStatusColor(status) {
  const normalized = `${status || ''}`.toLowerCase()
  if (['read', 'delivered', 'received', 'sent'].includes(normalized)) {
    return { color: '#047857', bg: 'rgba(5, 150, 105, 0.1)' }
  }
  if (['requested', 'queued', 'accepted', 'skipped'].includes(normalized)) {
    return { color: '#475569', bg: 'rgba(100, 116, 139, 0.1)' }
  }
  if (['failed', 'undelivered'].includes(normalized)) {
    return { color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.1)' }
  }
  return { color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)' }
}

// Friendly title for a support-agent log action.
function humanizeAction(actionType) {
  switch (`${actionType || ''}`) {
    case 'call-outcome':
    case 'custom-outcall':
      return 'Call'
    case 'call-heads-up':
      return 'Call heads-up'
    case 'email-intent':
      return 'Email'
    case 'free-form':
    case 'custom-whatsapp':
      return 'WhatsApp message'
    case 'inbound-reply':
      return 'Reply received'
    case 'send-google-review':
      return 'Google review'
    case 'permission-template':
      return 'Conversation opener'
    default:
      return 'Interaction'
  }
}

function formatDuration(seconds) {
  const safe = Math.max(0, Number(seconds) || 0)
  if (!safe) {
    return ''
  }
  const minutes = Math.floor(safe / 60)
  const secs = safe % 60
  return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`
}

function formatLogTime(value) {
  if (!value) {
    return ''
  }
  const parsed = dayjs.utc(value).local()
  if (!parsed.isValid()) {
    return ''
  }
  return `${parsed.fromNow()} · ${parsed.format('DD MMM, h:mm A')}`
}

// Reverse-chronological support-agent interaction history (whatsapp / email / voice incl. logged
// call outcomes) for the contact, from GET /contact-actions/history. Loads on mount — the surface
// only mounts this when a call surface is actually open (no per-card polling).
export default function SupportCallHistoryPanel({ patientId, practitionerId, bookedEventId, clinicBrandId, clinicId, targetRole, eventType }) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [limit, setLimit] = useState(PAGE_SIZE)

  const load = useCallback(
    async (limitToLoad) => {
      if (!patientId && !practitionerId) {
        return
      }
      setLoading(true)
      setError('')
      const result = await getSupportContactHistory({
        patientId,
        practitionerId,
        bookedEventId,
        clinicBrandId,
        clinicId,
        targetRole,
        eventType,
        limit: limitToLoad,
      })
      if (result?.ok) {
        setLogs(result.logs || [])
      } else {
        setError(result?.message || 'Unable to load communication history.')
        setLogs([])
      }
      setLoading(false)
    },
    [patientId, practitionerId, bookedEventId, clinicBrandId, clinicId, targetRole, eventType],
  )

  useEffect(() => {
    setLimit(PAGE_SIZE)
    load(PAGE_SIZE)
  }, [load])

  // A full page back suggests there may be more; bump the limit and refetch.
  const hasMore = useMemo(() => logs.length >= limit, [logs.length, limit])

  function handleShowMore() {
    const nextLimit = limit + PAGE_SIZE
    setLimit(nextLimit)
    load(nextLimit)
  }

  return (
    <Stack spacing={1.25} sx={{ height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#475569' }}>
          {logs.length > 0 ? `${logs.length}${hasMore ? '+' : ''} previous interaction${logs.length === 1 ? '' : 's'}` : 'Previous interactions'}
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshRoundedIcon sx={{ fontSize: 15 }} />}
          onClick={() => load(limit)}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b', minWidth: 0 }}
        >
          Refresh
        </Button>
      </Stack>

      {loading && logs.length === 0 ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={22} />
        </Stack>
      ) : error && logs.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: '#b91c1c', py: 2 }}>{error}</Typography>
      ) : logs.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
            No previous WhatsApp, email or call activity for this contact yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {logs.map((log, index) => {
            const channelMeta = getChannelMeta(log?.channel)
            const statusStyle = getStatusColor(log?.status)
            const title = log?.messagePreview || humanizeAction(log?.actionType)
            const durationLabel = formatDuration(log?.metadata?.callDurationSeconds)
            const byLabel = log?.supportAgentName ? `by ${log.supportAgentName}` : ''
            return (
              <Box
                key={log?.id || `${log?.createdAt || ''}-${index}`}
                sx={{
                  p: 1.1,
                  borderRadius: '12px',
                  border: '1px solid rgba(226, 232, 240, 0.9)',
                  backgroundColor: '#ffffff',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '9px',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      color: channelMeta.color,
                      backgroundColor: `${channelMeta.color}14`,
                    }}
                  >
                    {channelMeta.icon}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', minWidth: 0 }} noWrap>
                        {title}
                      </Typography>
                      {log?.status ? (
                        <Chip
                          size="small"
                          label={log.status}
                          sx={{
                            height: 18,
                            fontSize: 10.5,
                            fontWeight: 700,
                            textTransform: 'capitalize',
                            color: statusStyle.color,
                            backgroundColor: statusStyle.bg,
                          }}
                        />
                      ) : null}
                      {durationLabel ? (
                        <Chip
                          size="small"
                          label={durationLabel}
                          sx={{ height: 18, fontSize: 10.5, fontWeight: 700, color: '#7c3aed', backgroundColor: 'rgba(124, 58, 237, 0.1)' }}
                        />
                      ) : null}
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.25 }}>
                      {[channelMeta.label, byLabel].filter(Boolean).join(' · ')}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#cbd5e1', mt: 0.15 }}>
                      {formatLogTime(log?.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )
          })}

          {hasMore ? (
            <Button
              size="small"
              onClick={handleShowMore}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#4f46e5', alignSelf: 'center' }}
            >
              {loading ? 'Loading…' : 'Show more'}
            </Button>
          ) : null}
        </Stack>
      )}
    </Stack>
  )
}
